import React, { useState, useEffect } from "react";
import Page from "@atlaskit/page";
import Textfield from "@atlaskit/textfield";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { setEditorSaved } from "../../../../../redux/actions/integrationActions";
import { merge } from "lodash";
import ProjectSelectList from "../ProjectSelectList";
import IssueTypeSelectList from "../IssueTypeSelectList";
import DescriptionBox from "./template/DescriptionBox";
import CustomFieldTable from "./template/CustomFieldTable";
import Tabs, { Tab, TabList, TabPanel } from "@atlaskit/tabs";
import ConditionsPage from "../../../../../integrations/flow/edit/action/tabs/conditionsTab/ConditionsPage";
import VariablesPage from "../../../../../integrations/flow/edit/action/tabs/variablesTab/VariablesPage";
import IteratorInput from "../../../../../integrations/flow/edit/action/tabs/behavioursTab/IteratorInput";
import GetHelpExtensions from "../../../../../integrations/flow/edit/action/extension/GetHelpExtensions";
import MenuSectionExtensions from "../../../../../integrations/flow/edit/action/extension/MenuSectionExtensions";
import ActionMenu from "../../../../../integrations/flow/edit/action/menu/ActionMenu";
import ActionStatusToggle from "../../../../../integrations/flow/edit/action/ActionStatusToggle";

const CreateIssueActionPage = () => {
  const dispatch = useDispatch();
  const flow = useSelector((state) => state.integration.flow);
  const { sectionId } = useParams();
  const [currentAction, setCurrentAction] = useState(null);
  const [currentBody, setBody] = useState({ fields: {} });
  const [summary, setSummary] = useState("");
  const [selectedProjectId, setSelectedProjectId] = useState(undefined);
  const [selectedIssueType, setSelectedIssueType] = useState(undefined);

  useEffect(() => {
    if (flow.actions) {
      const action = flow.actions.find((a) => a.id === sectionId);
      if (action) {
        setCurrentAction(action);
        const tmpBody = toObject(action.body);
        setBody(tmpBody);
        setSummary(tmpBody.fields?.summary || "");
        setSelectedProjectId(tmpBody.fields?.project?.id);
        setSelectedIssueType(tmpBody.fields?.issuetype?.id);
      }
    }
  }, [sectionId, flow]);

  useEffect(() => {
    mergeCurrentActionToFlow();
  }, [currentBody, currentAction, selectedProjectId, selectedIssueType, summary]);

  const mergeCurrentActionToFlow = () => {
    if (!currentAction) {
      return;
    }
    const actionIndex = flow.actions.findIndex((a) => a && a.id === sectionId);

    if (actionIndex > -1) {
      flow.actions[actionIndex] = currentAction;
    }
  };

  const selectProject = (projectId) => {
    const project = { fields: { project: { id: projectId } } };
    updateBody(project);
    setSelectedProjectId(projectId);
  };

  const selectIssueType = (issueType) => {
    const issueTypeObj = { fields: { issuetype: { id: issueType } } };
    updateBody(issueTypeObj);
    setSelectedIssueType(issueType);
  };

  const onChangeSummary = (e) => {
    const newSummary = e.target.value;
    const summaryBody = { fields: { summary: newSummary } };
    setSummary(newSummary);
    updateBody(summaryBody);
  };

  const updateBody = (newFields) => {
    const updatedBody = merge({}, currentBody, newFields);
    setBody(updatedBody);
    setCurrentAction((prevAction) => ({
      ...prevAction,
      body: JSON.stringify(updatedBody),
    }));
    dispatch(setEditorSaved(false));
  };

  const toObject = (body) => {
    try {
      return body ? JSON.parse(body) : { fields: {} };
    } catch {
      return { fields: {} };
    }
  };

  return (
    <Page>
      <Tabs>
        <TabList>
          <Tab>Request Configuration</Tab>
          <Tab>Conditions</Tab>
          <Tab>Variables</Tab>
          <Tab>Behaviour</Tab>
        </TabList>
        <TabPanel>
          <div className="ihub-action-page">
            <div className="ihub-webaction-header">
              <h4>Action: Create Issue</h4>
              <div className="ihub-actionmenu-action">
                <GetHelpExtensions />
                <MenuSectionExtensions action={currentAction} />
                <ActionMenu action={currentAction} />
                <ActionStatusToggle action={currentAction} flow={flow} />
              </div>
            </div>
            <label>
              Project<span className="ihub-required">*</span>
            </label>
            <ProjectSelectList onSelectProject={selectProject} selectedProjectId={selectedProjectId} />
            <label>
              Issue Type<span className="ihub-required">*</span>
            </label>
            <IssueTypeSelectList onSelectIssueType={selectIssueType} selectedIssueType={selectedIssueType} />
            <label>
              Summary<span className="ihub-required">*</span>
            </label>
            <Textfield name="summary" isCompact={true} onChange={onChangeSummary} value={summary} />
            <DescriptionBox currentBody={currentBody} update={updateBody} />
            <CustomFieldTable body={currentBody} update={updateBody} isCreateForm={true} />
          </div>
        </TabPanel>
        <TabPanel>
          <div className="ihub-log-tab-warpper">
            <ConditionsPage action={currentAction} flow={flow} />
          </div>
        </TabPanel>
        <TabPanel>
          <div className="ihub-log-tab-warpper">
            <VariablesPage action={currentAction} />
          </div>
        </TabPanel>
        <TabPanel>
          <div className="ihub-log-tab-warpper">
            <IteratorInput action={currentAction} />
          </div>
        </TabPanel>
      </Tabs>
    </Page>
  );
};

export default CreateIssueActionPage;
